import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
} from 'material-react-table';
import {
  Box,
  Button,
  createTheme,
  ThemeProvider,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import Modal from 'react-modal';
import { readableStampSimpleMonthYear } from '../../utils/dateFunctions';
import ReportWorkFlowVehiclesModal from './ReportWorkFlowVehiclesModal';
import ReportDepartmentDetailsModal from './ReportDepartmentDetailsModal';
import GroupWorkFlowAveragesChart from './GroupWorkFlowAveragesChart';
import NestedHistoryTable from './NestedHistoryTable';
import {
  formatDaysHours,
  getTotalMinutes,
  computeAverageTime,
  getGroupWorkFlowAverageForRange,
} from './chartUtils';
import _ from 'lodash';

// Utility to get the last six months in 'YYYY_MM' format
const getLastSixMonths = () => {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    months.push(`${year}_${month}`);
  }
  return months;
};

const TableMonthly = ({ data, dealership_report_hours }) => {
  const {
    report_dealerships,
    report_department_data,
    workflows,
    related_installations,
  } = useSelector((state) => ({
    report_dealerships: state.settings_reports.report_dealerships || '',
    report_department_data: state.settings_reports.report_department_data || [],
    workflows: state.settings_departments.work_flows || [],
    related_installations: state.settings_dealerships.related_installations,
  }));

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState({ report_id: 0, department_id: 0, dealership: '' });
  const [selectedRange, setSelectedRange] = useState(null);

  const openGroupModal = (range, department_id, dealership) => {
    if (!range || !(['7', '14', '30', '90'].includes(range) || /^[0-9]{4}_[0-9]{2}$/.test(range))) {
      return;
    }
    setSelectedReport(prev => ({ ...prev, department_id, dealership }));
    setSelectedRange(range);
    setGroupModalOpen(true);
  };

  const closeGroupModal = () => {
    setGroupModalOpen(false);
    setSelectedRange(null);
  };

  const tableTheme = useMemo(() => createTheme({
    palette: { mode: 'light' },
  }), []);

  const columnHelper = createMRTColumnHelper();

  const columns = [
    columnHelper.accessor('dealership', { header: 'Dealership', size: 120 }),
    columnHelper.accessor('department', {
      header: 'WorkFlow',
      accessorFn: (row) => {
        const workflow = workflows.find(w => parseInt(w.id) === parseInt(row.department_id));
        return workflow ? workflow.name || 'Unknown' : 'Unknown';
      },
    }),
    columnHelper.accessor('report_month', { header: 'Report Month', size: 100 }),
    columnHelper.accessor('average_time', {
      header: 'Avg Days',
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span>{formatDaysHours(row.original.average_time) || 'N/A'}</span>
        </Box>
      ),
    }),
    columnHelper.accessor('number_of_vehicles', { header: 'Vehicle Count' }),
    columnHelper.accessor('details', {
      header: 'Details',
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <ReportWorkFlowVehiclesModal department_id={row.original.department_id} report_id={row.original.report_id} />
        </Box>
      ),
    }),
  ];

  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const year = now.getFullYear();
  const filename = `monthly_workflow_report_${month}_${day}_${year}`;

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename,
  });

  const exportableColumnKeys = ['dealership', 'department', 'report_month', 'average_time', 'number_of_vehicles'];

  const reportDealerships = useMemo(() => (
    report_dealerships ? report_dealerships.split(',').filter(d => d.trim()) : Object.keys(data.yAxis || {})
  ), [report_dealerships, data]);

  const tableData = useMemo(() => {
    if (!data || !reportDealerships.length || !data.xAxis || !Array.isArray(data.xAxis)) {
      return [];
    }
    return reportDealerships.flatMap((dealership) => {
      if (!data.yAxis?.[dealership]) return [];
      return data.xAxis.map((month, index) => {
        const number_of_vehicles = data.xCount?.[dealership]?.[index] || 0;
        if (number_of_vehicles > 0) {
          const reportId = data.dataId?.[dealership]?.[index] || '';
          return {
            dealership,
            department: data.department?.[dealership]?.[index] || '',
            report_month: month || '',
            average_time: data.yAxis?.[dealership]?.[index] || 0,
            average_time_24_5: data.rawtime_24_5?.[dealership]?.[index] || 0,
            average_time_24_6: data.rawtime_24_6?.[dealership]?.[index] || 0,
            report_id: reportId,
            department_id: data.departmentId?.[dealership]?.[index] || '',
            goal: data.goal?.[dealership]?.[index] || '',
            number_of_vehicles,
            vehicles: [],
          };
        }
        return null;
      }).filter(item => item !== null);
    });
  }, [data, reportDealerships]);

  const handleExportToCSV = () => {
    const rowData = tableData.map(item => exportableColumnKeys.reduce((acc, key) => {
      acc[key] = key === 'department' ? (workflows.find(w => parseInt(w.id) === parseInt(item.department_id))?.name || 'Unknown') :
                key === 'average_time' ? formatDaysHours(item.average_time) :
                item[key] || '';
      return acc;
    }, {}));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ['Dealership', 'WorkFlow', 'Report Month', 'Average Time', 'Number of Vehicles'];
    const tableRows = tableData.map(item => [
      item.dealership,
      workflows.find(w => parseInt(w.id) === parseInt(item.department_id))?.name || 'Unknown',
      item.report_month,
      formatDaysHours(item.average_time),
      item.number_of_vehicles,
    ]);
    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 10 });
    doc.save(filename);
  };

  const handleExportToPDFwGraph = () => {
    const doc = new jsPDF();
    const tableColumn = ['Dealership', 'WorkFlow', 'Report Month', 'Average Time', 'Number of Vehicles'];
    const tableRows = tableData.map(item => [
      item.dealership,
      workflows.find(w => parseInt(w.id) === parseInt(item.department_id))?.name || 'Unknown',
      item.report_month,
      formatDaysHours(item.average_time),
      item.number_of_vehicles,
    ]);
    const summaryElement = document.getElementById('report-summary');
    const summaryText = summaryElement ? summaryElement.textContent : '';
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const maxTextWidth = pageWidth - margin * 2;
    const wrappedText = doc.splitTextToSize(summaryText, maxTextWidth);
    doc.setFontSize(12);
    doc.text(wrappedText, margin, 10);
    const textHeight = wrappedText.length * 10;
    const startYAfterText = textHeight + 10;
    const element = document.getElementById('monthly-chart');
    if (element) {
      html2canvas(element).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const imgProps = doc.getImageProperties(imgData);
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        doc.addImage(imgData, 'PNG', 0, startYAfterText, pdfWidth, pdfHeight);
        autoTable(doc, { head: [tableColumn], body: tableRows, startY: startYAfterText + pdfHeight + 10 });
        doc.save(filename);
      });
    } else {
      autoTable(doc, { head: [tableColumn], body: tableRows, startY: startYAfterText + 10 });
      doc.save(filename);
    }
  };

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableRowSelection: false,
    enableExpanding: true,
    enablePagination: false,
    positionToolbarAlertBanner: 'bottom',
    initialState: { showColumnFilters: false, expanded: false },
    renderDetailPanel: ({ row }) => {
      const isExpanded = row.getIsExpanded();

      const lastSixMonths = getLastSixMonths();
      const nestedData = isExpanded ? (report_department_data || [])
        .filter(item => {
          const monthYear = item.report_type?.replace('work_flow_', '');
          return item.dealership === row.original.dealership &&
                 item.work_flow_id === row.original.department_id &&
                 lastSixMonths.includes(monthYear);
        })
        .map(item => ({
          dealership: item.dealership || '',
          department: workflows.find(w => parseInt(w.id) === parseInt(item.work_flow_id))?.name || 'Unknown',
          report_month: readableStampSimpleMonthYear(item.recon_end_date) || '',
          average_time: computeAverageTime(item, dealership_report_hours),
          average_time_dhm: formatDaysHours(computeAverageTime(item, dealership_report_hours)) || 'N/A',
          average_time_minutes: getTotalMinutes(computeAverageTime(item, dealership_report_hours)) || 0,
          number_of_vehicles: Number(item.vehicle_count) || 0,
          report_id: item.id || '',
          department_id: item.work_flow_id || '',
        })) : [];

      const timeRanges = ['7', '14', '30', '90'];
      const metricsRef = React.useRef(null);

      const handleExportDetailToPDF = () => {
        const doc = new jsPDF();
        const now = new Date();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const year = now.getFullYear();
        const workflowName = workflows.find(w => parseInt(w.id) === parseInt(row.original.department_id))?.name || 'Unknown';
        const filename = `workflow_summary_${month}_${day}_${year}.pdf`;

        // Add title
        doc.setFontSize(16);
        doc.text(`${workflowName} Summary`, 10, 10);

        // Capture metrics boxes
        if (metricsRef.current) {
          html2canvas(metricsRef.current).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const imgProps = doc.getImageProperties(imgData);
            const pdfWidth = doc.internal.pageSize.getWidth() - 20;
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            doc.addImage(imgData, 'PNG', 10, 20, pdfWidth, pdfHeight);

            // Add nested table
            if (nestedData.length > 0) {
              const tableColumn = ['Dealership', 'WorkFlow', 'Month', 'Avg Time', 'Vehicle Count'];
              const tableRows = nestedData.map(item => [
                item.dealership,
                item.department,
                item.report_month,
                item.average_time_dhm,
                item.number_of_vehicles,
              ]);
              autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 20 + pdfHeight + 10,
                styles: { fontSize: 10 },
              });
            }

            doc.save(filename);
          }).catch(() => {});
        }
      };

      const metricsHeader = isExpanded ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
            <Button
              onClick={handleExportDetailToPDF}
              startIcon={<FileDownloadIcon />}
              variant="contained"
              color="primary"
              size="small"
            >
              Export to PDF
            </Button>
          </Box>
          <Box ref={metricsRef} sx={{ padding: '8px', backgroundColor: '#f5f5f5', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#007bff' }}>
              {`${workflows.find(w => parseInt(w.id) === parseInt(row.original.department_id))?.name || 'Unknown'} Summary`}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '8px', flexWrap: 'wrap' }}>
              {timeRanges.map((range) => {
                const reportType = `work_flow_${range}`;
                const filteredData = report_department_data.filter(item =>
                  item.dealership === row.original.dealership &&
                  item.work_flow_id === row.original.department_id &&
                  item.report_type === reportType
                );
                const deptAvg = filteredData.length > 0 ? filteredData.reduce((sum, item) => {
                  const avg = computeAverageTime(item, dealership_report_hours);
                  return sum + (isNaN(avg) ? 0 : avg);
                }, 0) / filteredData.length : 0;
                const vehicleCount = filteredData.reduce((sum, item) => sum + (Number(item.vehicle_count) || 0), 0);
                const groupAverage = getGroupWorkFlowAverageForRange(range, report_department_data, dealership_report_hours, null, row.original.department_id);
                const reportId = filteredData.length > 0 ? filteredData[0].id : 0;
                return (
                  <Box
                    key={range}
                    sx={{
                      flex: '1 1 22%',
                      minWidth: '200px',
                      padding: '12px',
                      border: '2px solid #ccc',
                      borderRadius: '8px',
                      backgroundColor: (timeRanges.indexOf(range) % 2 === 0) ? '#f8f9fa' : '#e9ecef',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': { backgroundColor: '#dee2e6', transform: 'translateY(-2px)' },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {`${range}-Day Avg`}
                    </Typography>
                    <Typography sx={{ color: deptAvg ? '#28a745' : '#dc3545' }}>
                      {deptAvg ? formatDaysHours(deptAvg) : 'N/A'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer' }}>
                      Vehicles: {vehicleCount || 0}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '8px', mt: 1 }}>
                      <ReportWorkFlowVehiclesModal department_id={row.original.department_id} report_id={reportId} />
                      <ReportDepartmentDetailsModal work_flow_id={row.original.department_id} report_id={reportId} />
                    </Box>
                    {related_installations && related_installations.length > 1 && (
                                          <div
                                            onClick={() => openGroupModal(range, row.original.department_id, row.original.dealership)}
                                            className="btn btn-large btn-success btn-block space-top-1 text-center"
                                          >
                                            Group Avg: {groupAverage ? formatDaysHours(groupAverage) : 'N/A'}
                                          </div>
                                        )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      ) : null;

      const nestedColumns = [
        columnHelper.accessor('dealership', { header: 'Dealership', size: 120 }),
        columnHelper.accessor('department', { header: 'WorkFlow' }),
        columnHelper.accessor('report_month', { header: 'Month', size: 100 }),
        columnHelper.accessor('average_time', {
          header: 'Avg Time',
          Cell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span>{row.original.average_time_dhm}</span>
            </Box>
          ),
        }),
        columnHelper.accessor('number_of_vehicles', { header: 'Vehicle Count' }),
        columnHelper.accessor('details', {
          header: 'Details',
          Cell: ({ row }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <ReportWorkFlowVehiclesModal department_id={row.original.department_id} report_id={row.original.report_id} />
            </Box>
          ),
        }),
      ];

      if (!isExpanded) return <div />;

      return (
        <Box sx={{ backgroundColor: '#f5f5f5', padding: '16px' }}>
          {metricsHeader}
          <NestedHistoryTable columns={nestedColumns} data={nestedData} />
        </Box>
      );
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: '16px', padding: '8px', flexWrap: 'wrap' }}>
        <Button onClick={handleExportToCSV} startIcon={<FileDownloadIcon />}>
          Export to CSV
        </Button>
        <Button onClick={handleExportToPDF} startIcon={<FileDownloadIcon />}>
          Export to PDF
        </Button>
        <Button onClick={handleExportToPDFwGraph} startIcon={<FileDownloadIcon />}>
          Export to PDF (Include Graph)
        </Button>
      </Box>
    ),
  });

  const total_vehicles = useMemo(() => {
    return tableData.reduce((sum, item) => sum + (item.number_of_vehicles > 0 ? item.number_of_vehicles : 0), 0);
  }, [tableData]);

  const total_times = useMemo(() => {
    return tableData.reduce((sum, item) => sum + (item.number_of_vehicles > 0 ? item.number_of_vehicles * (item.average_time || 0) : 0), 0);
  }, [tableData]);

  const total_average_times = useMemo(() => {
    const validItems = tableData.filter(item => item.number_of_vehicles > 0 && item.average_time);
    return validItems.length > 0 ? total_times / total_vehicles : 0;
  }, [total_times, total_vehicles]);

  const workflowName = useMemo(() => {
    const workflow = workflows.find(w => parseInt(w.id) === parseInt(selectedReport.department_id));
    return workflow ? (workflow.name || 'Unknown') : 'Unknown';
  }, [workflows, selectedReport.department_id]);

  return (
    <ThemeProvider theme={tableTheme}>
      <p className="text-center" id="report-summary">Combined Displayed Averages: {formatDaysHours(total_average_times)} / Vehicles: {total_vehicles}</p>
      <MaterialReactTable table={table} />
      <Modal
        isOpen={groupModalOpen}
        onRequestClose={closeGroupModal}
        contentLabel="Group Averages"
        className="customModal"
        ariaHideApp={false}
        overlayClassName="overlay"
      >
        <div className="customCard" style={{ width: '50%', margin: '0 auto' }}>
          <div className="customHeader" style={{ textAlign: 'center' }}>
            <button
              type="button"
              className="btn btn-danger btn-sm float-end space-left-2"
              aria-label="Close"
              onClick={closeGroupModal}
            >
              <span aria-hidden="true">×</span>
            </button>
            <Typography variant="h5">{selectedRange ? `${selectedRange}-day Group Averages (${workflowName})` : 'Loading...'}</Typography>
          </div>
          <Box sx={{ padding: '16px', textAlign: 'center' }}>
            <hr style={{ margin: '8px 0', width: '100%', border: '1px solid #ccc' }} />
            {selectedRange && report_department_data ? (
              <GroupWorkFlowAveragesChart
                range={selectedRange}
                reportDepartmentData={report_department_data}
                dealershipReportHours={dealership_report_hours}
                work_flow_id={selectedReport.department_id}
              />
            ) : (
              <Typography>Loading data...</Typography>
            )}
          </Box>
        </div>
      </Modal>
    </ThemeProvider>
  );
};

export default React.memo(TableMonthly);